import React, { useEffect, useState } from "react";
import {
  Box,
  Spinner,
  Text,
  useColorMode,
  useColorModeValue,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import API, { getMediaURL } from "../../../context/API";
import CustomizationSideBox from "./CustomizationSideBox";

function CustomizationsForOverview({
  selectedCustomizationId,
  tabIndex,
  onCloseSide,
  isEditOption,
  customization
}) {
  const [customizations, setCustomizations] = useState([]);
  const [filteredCustomizations, setFilteredCustomizations] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customizationData, setCustomizationData] = useState(customization || null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [previouslySelectedCustId, setPreviouslySelectedCustId] =
    useState(null);

  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { colorMode } = useColorMode();
    const borderColor = useColorModeValue("gray.300", "gray.700");
  useEffect(() => {
    if (tabIndex === 5) { 
      fetchCustomizations();
    }
  }, [tabIndex]);

  useEffect(() => {
    if (selectedCustomizationId && customizations.length > 0) {
      const customization = customizations.find(
        (c) => c.id.toString() === selectedCustomizationId.toString()
      );
      if (customization) {
        setSelectedItem(customization.id);
        setCustomizationData({
          customization: customization.customization,
          options: customization.option,
          note: customization.note,
          max_quantity: customization.max_quantity,
          min_quantity: customization.min_quantity,
          multiple_selections: customization.multiple_selections,
          sold_out: customization.sold_out,
          multiple_selections_quantity:
            customization.multiple_selections_quantity,
          related_items: customization.related_items || [],
        });
        onOpen(); // Open the side box
      }
    }
  }, [selectedCustomizationId, customizations]);

  const fetchCustomizations = async () => {
    try {
      setLoading(true);
      const response = await API.get("/customizations");
      const customizationsData = response.data.data.map((customization) => ({
        ...customization,
        options: customization.option.map((option) => ({
          ...option,
          item: {
            ...option.item,
          },
        })),
        related_items: customization.related_items.map((item) => ({
          ...item,
          picture: getMediaURL(item.picture), // Handle related item image URL
        })),
      }));

      customizationsData.sort((a, b) => a.id - b.id);
      if (response.data.success) {
        setCustomizations(customizationsData);
        setFilteredCustomizations(customizationsData);
        setLastUpdated(new Date().toLocaleString());
        return customizationsData;
      } else {
        setError("Failed to fetch customizations.");
        return [];
      }
    } catch (error) {
      console.error("error:", error);
      setError("An error occurred while fetching customizations.");
      return [];
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    const currentSelectedCustId = selectedItem;
    setPreviouslySelectedCustId(currentSelectedCustId);

    try {
      const fetchedCustomizations = await fetchCustomizations();
      if (currentSelectedCustId) {
        const customization = fetchedCustomizations.find(
          (c) => c.id === currentSelectedCustId
        );
        if (customization) {
          handleItemClick(customization);
        } else {
          setSelectedItem(null);
          setCustomizationData(null);
          onClose();
          onCloseSide()
          toast({
            title: "Customization Not Found",
            description:
              "The previously selected customization was not found after refresh.",
            status: "warning",
            duration: 5000,
            isClosable: true,
          });
        }
      }
    } catch (error) {
      console.error("Error during refresh:", error);
      toast({
        title: "Refresh Failed",
        description: "Unable to refresh customizations. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setRefreshing(false);
    }
  };

  const handleItemClick = (customization) => {
    setSelectedItem(customization.id);
    setCustomizationData({
      customization: customization.customization,
      options: customization.option,
      note: customization.note,
      max_quantity: customization.max_quantity,
      sold_out: customization.sold_out,
      min_quantity: customization.min_quantity,
      multiple_selections: customization.multiple_selections,
      multiple_selections_quantity: customization.multiple_selections_quantity,
      related_items: customization.related_items || [],
    });
  };

  const handleInputChange = (field, value) => {
    setCustomizationData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
    if (field === "sold_out") {
      handleSubmit("customizationrules");
    }
  };

  const handleEdit = (name) => {
    setCustomizationData({
      ...customizationData,
      customization: name,
    });
  };

  const handleDelete = async () => {
    try {
      await API.delete(`/customizations/${selectedItem}`);
      setCustomizations(
        customizations.filter((item) => item.id !== selectedItem)
      );
      setFilteredCustomizations(
        filteredCustomizations.filter((item) => item.id !== selectedItem)
      );
      toast({
        title: "Customization deleted.",
        description: "The customization has been deleted successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      onClose();
      onCloseSide()
    } catch (error) {
      console.error("Error deleting customization:", error);
    }
  };

  const handleDuplicate = async () => {
    try {
      const postResponse = await API.get(
        `/customizations/clone/${selectedItem}`
      );
      toast({
        title: "Customization created.",
        description: "The customization has been created successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      const newCustomization = postResponse.data.data;
      const updatedCustomizations = [...customizations, newCustomization];
      setCustomizations(updatedCustomizations);
      setFilteredCustomizations(updatedCustomizations);
      handleItemClick(newCustomization);
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleSeeChanges = () => {
    console.log("See changes for customization");
  };

  const handleSubmit = async (submissionType, selectedOptions) => {
    const formattedCustomizationData = {
      customization: customizationData.customization,
      options: customizationData.options.map((option) => {
        if (typeof option === "object" && option !== null && "id" in option) {
          return option.id.toString();
        }
        return option.toString();
      }),

      related_items: (customizationData.related_items || [])
        .map((relatedItem, index) => ({
          id:
            relatedItem.item && relatedItem.item.id
              ? relatedItem.item.id.toString()
              : "",
          sequence_number: relatedItem.sequence_number || index.toString(),
        }))
        .filter((item) => item.item !== ""),
      max_quantity: customizationData.max_quantity,
      min_quantity: customizationData.min_quantity,
      multiple_selections: customizationData.multiple_selections,

      multiple_selections_quantity: Number(
        customizationData.multiple_selections
          ? customizationData.multiple_selections_quantity || 1
          : 1
      ),
      sold_out: customizationData.sold_out,
    };

    try {
      if (selectedItem) {
        let putResponse;
        if (submissionType === "name") {
          putResponse = await API.put(`/customizations/${selectedItem}/name`, {
            customization: customizationData.customization,
          });
        }
        if (submissionType === "options") {
          putResponse = await API.put(
            `/customizations/${selectedItem}/options`,
            {
              options: selectedOptions.map((option) => {
                if (
                  typeof option === "object" &&
                  option !== null &&
                  "id" in option
                ) {
                  return {
                    id: option.id.toString(),
                    sequence_number: option.sequence_number,
                  };
                }
                return option.toString();
              }),
            }
          );
        }
        if (submissionType === "addtoitems") {
          putResponse = await API.put(`/customizations/${selectedItem}/items`, {
            related_items: (customizationData.related_items || [])
              .map((relatedItem, index) => ({
                id:
                  relatedItem.item && relatedItem.item.id
                    ? relatedItem.item.id.toString()
                    : "",
                sequence_number:
                  relatedItem.sequence_number || index.toString(),
              }))
              .filter((item) => item.item !== ""),
          });
        }
        if (submissionType === "customizationrules") {
          putResponse = await API.put(`/customizations/${selectedItem}/rules`, {
            max_quantity: customizationData.max_quantity,
            min_quantity: customizationData.min_quantity,
            multiple_selections: customizationData.multiple_selections,
            sold_out: !customizationData.sold_out,
            multiple_selections_quantity: Number(
              customizationData.multiple_selections
                ? customizationData.multiple_selections_quantity || 1
                : 1
            ),
          });
        }
        const updatedCustomization = {
          ...putResponse.data.data,
          options: customizationData.options,
          related_items: customizationData.related_items,
        };

        const updatedCustomizations = customizations.map((c) =>
          c.id === selectedItem ? updatedCustomization : c
        );
        setCustomizations(updatedCustomizations);
        setFilteredCustomizations(updatedCustomizations);

        toast({
          title: "Customization updated.",
          description: "The customization has been updated successfully.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else {
        formattedCustomizationData.related_items = [{}];
        const postResponse = await API.post(
          "/customizations",
          formattedCustomizationData
        );
        const newCustomization = postResponse.data.data;

        toast({
          title: "Customization created.",
          description: "The customization has been created successfully.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });

        const updatedCustomizations = [...customizations, newCustomization];
        setCustomizations(updatedCustomizations);
        setFilteredCustomizations(updatedCustomizations);

        handleItemClick(newCustomization);
      }
    } catch (error) {
      console.error("Error saving customization:", error);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <Spinner size="xl" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <Text color="red.500">{error}</Text>
      </Box>
    );
  }
  return (
    (customizationData && selectedCustomizationId && (isOpen || isEditOption)) && (
      <Box
        position="sticky"
        top="20"
        maxHeight={`calc(100vh - var(--chakra-space-40))`}
        overflowY="auto"
        w="100%"
        p={4}
        pt={0}
        ml={4}
        bg={colorMode === "light" ? "white" : "gray.800"}
        borderRadius="8"
      >
        <CustomizationSideBox
          customizationData={customization || customizationData}
          handleInputChange={handleInputChange}
          handleDelete={handleDelete}
          handleDuplicate={handleDuplicate}
          handleSeeChanges={handleSeeChanges}
          onClose={()=>{
            onClose()
            onCloseSide()
          }}
          isOpen={isOpen}
          setCustomizationData={setCustomizationData}
          handleEdit={handleEdit}
          handleSubmit={handleSubmit}
          isNewCustomization={selectedItem === null}
          selectedItem={selectedItem}
          handleRefresh={handleRefresh}
          width={"100%"}
          isEditOption={isEditOption}
        />
      </Box>
    )
  );
}

export default CustomizationsForOverview;
